import React, { useMemo } from 'react';
import MUITextField from './fields/MUITextField';
import { get } from 'lodash';
import MUITextAreaField from './fields/MUITextAreaField';
import { T } from '../../../../utils/helper';
import { Divider } from '@mui/material';
import MUINumberField from './fields/MUINumberField';
import MUISelectField from './fields/MUISelectField';
import MUIDateField from './fields/MUIDateField';
import MUICheckboxField from './fields/MUICheckboxField';
import MUIRadioField from './fields/MUIRadioField';
import MUIComponentGroups from './fields/MUIComponentGroups';
import TextEditorField from './fields/TextEditorField';
import MUIAutocomplete from './fields/MUIAutocomplete';
import MultiUploadField from './fields/MultiUploadField';
import SingleUpload from './fields/SingleUpload';
import MarkdownEditor from './fields/MarkdownEditor';
import EditableTable from './fields/EditableTable';
import { useApplication } from '../../../../context/ApplicationContext';
import { findErrorByKey } from '../../../../lib/validation/validationHelperFunction';

interface FormErrors {
	path: string;
	message: string;
}
interface FormFieldsProps {
	type: string;
	fieldType?: string;
	formValues: Record<string, any>;
	onChange: (path: string, value: any) => void;
	formErrors?: Record<string, any>;
	editableTablePathWIthIndex?: string;
	readOnly?: boolean;
	disabled?: boolean;
	[key: string]: any; // Allows additional props to be passed
}

const FormFields: React.FC<FormFieldsProps> = (props) => {
	// // const COMPONENT_TABLE = 'componentTable';
	const { errors, tree } = useApplication();
	const {
		type,
		fieldType,
		formValues,
		onChange,
		formErrors,
		editableTablePathWIthIndex,
		readOnly,
		disabled,
		inversedBy,
		...restProps
	} = props;
	const dataPathParts = get(props, 'path', '').split('.');
	const fieldLabel = T(dataPathParts.length > 0 ? dataPathParts[dataPathParts.length - 1] : '');
	const isDisabled = get(props, 'disabled', '') === false ? false : disabled || readOnly;

	let errorInField = Boolean(
		(Array.isArray(errors) ? errors : []).find((e) => {
			return editableTablePathWIthIndex
				? get(e, 'path', '') === editableTablePathWIthIndex
				: get(e, 'path', '') === get(props, 'path', '');
		}),
	);
	let showError = useMemo(() => {
		return findErrorByKey(tree, editableTablePathWIthIndex ? editableTablePathWIthIndex : get(props, 'path', ''), type)
			? true
			: false;
	}, [tree, props]);

	errorInField = errorInField && showError;

	let errorMessage = '';
	if (errorInField) {
		errorMessage = get(
			(errors as FormErrors[]).find(
				(e) => get(e, 'path', '') === (editableTablePathWIthIndex ? editableTablePathWIthIndex : get(props, 'path', '')),
			),
			'message',
			'',
			(errors as FormErrors[]).find(
				(e) => get(e, 'path', '') === (editableTablePathWIthIndex ? editableTablePathWIthIndex : get(props, 'path', '')),
			),
			'message',
			'',
		);
	}

	const initialValue = useMemo(() => {
		const value = get(formValues, `${get(props, 'path')}`, '');
		return value || value === false ? value : '';
	}, [formValues, props]);

	const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		let value: any = null;
		if (e.target.value !== null) {
			if (typeof e.target.value === 'object' || Array.isArray(e.target.value) || typeof e.target.value === 'boolean') {
				value = e.target.value;
			} else {
				value = e.target.value ? e.target.value : null;
			}
		}
		onChange(get(props, 'path'), value);
	};

	switch (type) {
		case 'string':
			return (
				<MUITextField
					type={fieldType ? fieldType : 'text'}
					label={fieldLabel}
					fieldValue={initialValue}
					updateFieldValue={handleOnChange}
					isDisabled={isDisabled}
					error={errorInField}
					errorMessage={errorMessage}
					{...restProps}
				/>
			);
		case 'text':
			return (
				<MUITextAreaField
					label={fieldLabel}
					fieldValue={initialValue}
					updateFieldValue={handleOnChange}
					isDisabled={isDisabled}
					error={errorInField}
					errorMessage={errorMessage}
					{...restProps}
				/>
			);
		case 'textEditor':
			return (
				<TextEditorField
					label={fieldLabel}
					fieldValue={initialValue}
					updateFieldValue={handleOnChange}
					isDisabled={isDisabled}
					error={errorInField}
					errorMessage={errorMessage}
					{...restProps}
				/>
			);
		case 'richtext':
		case 'markdownEditor':
			return (
				<MarkdownEditor
					label={fieldLabel}
					fieldValue={initialValue}
					updateFieldValue={handleOnChange}
					isDisabled={isDisabled}
					error={errorInField}
					errorMessage={errorMessage}
					{...restProps}
				/>
			);
		case 'integer':
		case 'decimal':
			return (
				<MUINumberField
					label={fieldLabel}
					fieldValue={initialValue}
					updateFieldValue={handleOnChange}
					isDisabled={isDisabled}
					error={errorInField}
					errorMessage={errorMessage}
					{...restProps}
				/>
			);
		case 'enumeration':
		case 'singleSelect':
			return (
				<MUISelectField
					label={fieldLabel}
					fieldValue={initialValue}
					updateFieldValue={handleOnChange}
					isDisabled={isDisabled}
					error={errorInField}
					errorMessage={errorMessage}
					{...restProps}
				/>
			);
		case 'autocomplete':
			return (
				<MUIAutocomplete
					label={fieldLabel}
					fieldValue={initialValue}
					updateFieldValue={handleOnChange}
					isDisabled={isDisabled}
					error={errorInField}
					errorMessage={errorMessage}
					{...restProps}
				/>
			);
		case 'singleUpload':
			return (
				<SingleUpload
					label={fieldLabel}
					fieldValue={initialValue}
					updateFieldValue={handleOnChange}
					isDisabled={isDisabled}
					error={errorInField}
					errorMessage={errorMessage}
					{...restProps}
				/>
			);
		case 'media':
			if (get(props, 'multiple', false)) {
				return (
					<MultiUploadField
						label={fieldLabel}
						fieldValue={initialValue}
						updateFieldValue={handleOnChange}
						isDisabled={isDisabled}
						error={errorInField}
						errorMessage={errorMessage}
						{...restProps}
					/>
				);
			} else {
				return (
					<SingleUpload
						label={fieldLabel}
						fieldValue={initialValue}
						updateFieldValue={handleOnChange}
						isDisabled={isDisabled}
						error={errorInField}
						errorMessage={errorMessage}
						{...restProps}
					/>
				);
			}
		case 'componentMedia':
			return (
				<MultiUploadField
					label={fieldLabel}
					fieldValue={initialValue}
					updateFieldValue={handleOnChange}
					isDisabled={isDisabled}
					error={errorInField}
					errorMessage={errorMessage}
					{...restProps}
				/>
			);
		case 'datetime':
		case 'date':
			return (
				<MUIDateField
					label={fieldLabel}
					fieldValue={initialValue}
					updateFieldValue={handleOnChange}
					isDisabled={isDisabled}
					error={errorInField}
					errorMessage={errorMessage}
					{...restProps}
				/>
			);
		case 'checkbox':
		case 'boolean':
			return (
				<MUICheckboxField
					label={fieldLabel}
					fieldValue={initialValue}
					updateFieldValue={handleOnChange}
					isDisabled={isDisabled}
					error={errorInField}
					errorMessage={errorMessage}
					{...restProps}
				/>
			);
		case 'radio':
			return (
				<MUIRadioField
					label={fieldLabel}
					fieldValue={initialValue}
					updateFieldValue={handleOnChange}
					isDisabled={isDisabled}
					error={errorInField}
					errorMessage={errorMessage}
					{...restProps}
				/>
			);
		case 'component':
			return (
				<MUIComponentGroups
					label={fieldLabel}
					fieldValue={initialValue}
					updateFieldValue={handleOnChange}
					isDisabled={isDisabled}
					error={errorInField}
					errorMessage={errorMessage}
					{...restProps}
				/>
			);
		// case 'componentTable':
		// 	return (
		// 		<MUIComponentTable
		// 			label={fieldLabel}
		// 			fieldValue={initialValue}
		// 			updateFieldValue={handleOnChange}
		// 			isDisabled={isDisabled}
		// 			error={errorInField }
		// 			errorMessage={errorMessage}
		// 			{...restProps}
		// 		/>
		// 	);
		case 'componentTable':
		case 'editableTable':
			return (
				<EditableTable
					label={fieldLabel}
					fieldValue={initialValue}
					updateFieldValue={handleOnChange}
					isDisabled={isDisabled}
					error={errorInField}
					errorMessage={errorMessage}
					{...restProps}
				/>
			);

		// case 'infoBox':
		// 	return <MUITextInfo {...restProps} />;
		case 'divider':
			return <Divider sx={{ borderBottomWidth: '0.2rem' }} />;
		default:
			return (
				<MUITextField
					type={fieldType ? fieldType : 'text'}
					label={fieldLabel}
					fieldValue={initialValue}
					updateFieldValue={handleOnChange}
					isDisabled={isDisabled}
					error={errorInField}
					errorMessage={errorMessage}
					{...restProps}
				/>
			);
	}
};

export default FormFields;
